import React, { Component } from "react";
import {
	Box,
	Grid,
	Button,
	Divider,
	Backdrop,
	CircularProgress,
	Dialog,
	DialogContent,
} from "@mui/material";
import { makeStyles, withStyles } from "@mui/styles";
import { getImage } from "../../utils/image";
import { useBuyNumber } from "../../contexts/BuyNumberContext";
import agent from "../../agent/agent";
import { useSelector } from "react-redux";
import VisibleContent from "../VisibleContent";
import numberFormat from "../../utils/numberFormat";
import rateConvert from "../../utils/rateConvert";
import { notification, notificationTypes } from "../../utils/notification";
import getTrans from "../../languages/getTrans";

const MAX_ATTEMPTS = 600;
const RETRY_DELAY_MS = 1000;
const CRITICAL_ERRORS = [400, 401, 402, 403, 404];

class PopUpBuyNumber extends Component {
	state = {
		multiPrices: [],
		serviceInfo: null,
		currentPrice: null,
		isLoading: true,
		isBuying: false,
		currentAttempt: 0,
		isCancelled: false,
	};
	buyNumberTimeout = null;

	componentDidMount = async () => {
		await this.getServiceCountry();
	}
	componentWillUnmount = () => {
		if (this.buyNumberTimeout) {
			clearTimeout(this.buyNumberTimeout);
			this.buyNumberTimeout = null;
		}
	}

	getServiceCountry = async () => {
		const { config, userId } = this.props;
		const serviceSlug = config.serviceSlug || '';
		const countrySlug = config.countrySlug || '';
		const serviceInfo = await agent.get(`/api/service/get-service-data/${serviceSlug}?user=${userId}&country=${countrySlug}`)
			.then((res) => res.data)
			.catch(() => null);

		let multiPrices = (serviceInfo?.country?.prices || []).map((t) => t.value).filter((t) => !!t);
		if (multiPrices.length <= 0 || true) {
			const _price = Number.parseFloat(serviceInfo?.country?.price || '0');
			const funcCalc = (multiply) => {
				let _value = parseInt((_price * multiply) * 10000) / 10000;
				if (_value < Number.parseFloat(_price)) {
					return null;
				}
				return _value;
			};
			const calculatedPrices = [funcCalc(1), funcCalc(1.2), funcCalc(1.7)];
			const uniquePricesSet = new Set(calculatedPrices.map(p => p));
			multiPrices = calculatedPrices.filter(p => uniquePricesSet.has(p));
		}

		await this.setState({
			serviceInfo,
			isLoading: false,
			multiPrices: multiPrices.map((t) => ({ label: `$ ${t}`, value: t })),
			currentPrice: Number.parseFloat(config?.initPrice || serviceInfo?.country?.price || '0')
		}, async () => {
			await this.startRepeatBuyNumber();
		});
	}

	checkBuyNumberResponse = (response, currentAttempt) => {
		if (response?.error) {
			const errorType = response.error;
			if (errorType === "NO_BALANCE") {
				return {
					success: false,
					retry: false,
					errorMessage: getTrans('errors.Недостаточно средств')
				};
			}
			if (typeof errorType === 'object') {
				const errorResponse = errorType;
				const errorData = errorResponse?.data || {};
				const errorMessage = errorData?.message || getTrans('errors.Ошибка сервера');
				const statusCode = errorResponse?.status || errorResponse?.statusCode;

				if (statusCode && CRITICAL_ERRORS.includes(statusCode)) {
					return {
						success: false,
						retry: false,
						errorMessage: errorMessage
					};
				}
			}
			if (currentAttempt >= MAX_ATTEMPTS) {
				const errorMessage = typeof errorType === 'object'
					? (errorType?.data?.message || getTrans('errors.Ошибка сервера'))
					: getTrans('errors.Ошибка сервера');
				return {
					success: false,
					retry: false,
					errorMessage: errorMessage
				};
			}
			return {
				success: false,
				retry: true,
				errorMessage: null
			};
		}
		if (response && !response.error) {
			return {
				success: true,
				retry: false,
				errorMessage: null
			};
		}
		if (currentAttempt < MAX_ATTEMPTS) {
			return {
				success: false,
				retry: true,
				errorMessage: null
			};
		}

		return {
			success: false,
			retry: false,
			errorMessage: getTrans('errors.Ошибка сервера')
		};
	}
	startRepeatBuyNumber = async () => {
		const { serviceInfo, currentPrice, isBuying, currentAttempt, isCancelled } = this.state;
		if (isCancelled || isBuying) {
			return;
		}
		if (currentAttempt >= MAX_ATTEMPTS) {
			notification({
				type: notificationTypes.error,
				message: getTrans('errors.Превышено максимальное количество попыток')
			});
			await this.setState({ isBuying: false });
			return;
		}

		const countryMultiPrice = serviceInfo?.country?.prices || [];
		let params = [`maxPrice=${currentPrice}`];
		if (countryMultiPrice.length > 0) {
			const _multiPrice = countryMultiPrice.find((t) => Boolean(Number.parseFloat(t.value) === Number.parseFloat(currentPrice)));
			if (_multiPrice) {
				params.push(`providerIds=${_multiPrice.providerId}`);
			}
		}
		const apiUrl = `/api/sms-users/get-number/${serviceInfo?.country?.external_id}/${serviceInfo?.service?.external_id}?${params.join('&')}`;

		try {
			const nextAttempt = currentAttempt + 1;
			await this.setState({
				isBuying: true,
				currentAttempt: nextAttempt
			});

			let resBuyNumber;
			try {
				const res = await agent.get(apiUrl);
				if (res?.data === "NO_NUMBERS") {
					resBuyNumber = { error: 'NO_NUMBERS' };
				} else if (res?.data === "NO_BALANCE") {
					resBuyNumber = { error: 'NO_BALANCE' };
				} else {
					resBuyNumber = res.data;
				}
			} catch (err) {
				resBuyNumber = { error: err?.response || true };
			}

			const { isCancelled: cancelledAfterRequest } = this.state;
			if (cancelledAfterRequest) {
				await this.setState({ isBuying: false });
				this.closeBuy();
				return;
			}

			const responseCheck = this.checkBuyNumberResponse(resBuyNumber, nextAttempt);

			if (responseCheck.success) {
				notification({
					type: notificationTypes.success,
					message: getTrans('home.Номер успешно приобретен')
				});
				await this.setState({ isBuying: false });
				this.closeBuy();
				return;
			}

			if (!responseCheck.retry && responseCheck.errorMessage) {
				notification({
					type: notificationTypes.error,
					message: responseCheck.errorMessage
				});
				await this.setState({ isBuying: false });
				this.closeBuy();
				return;
			}

			if (responseCheck.retry) {
				const { isCancelled: cancelledBeforeRetry } = this.state;
				if (cancelledBeforeRetry) {
					await this.setState({ isBuying: false });
					this.closeBuy();
					return;
				}

				await this.setState({ isBuying: false });
				this.buyNumberTimeout = setTimeout(() => {
					this.startRepeatBuyNumber();
				}, RETRY_DELAY_MS);
				return;
			}
		} catch (error) {
			const { isCancelled: cancelledAfterError } = this.state;
			if (cancelledAfterError) {
				await this.setState({ isBuying: false });
				this.closeBuy();
				return;
			}

			const errorResponse = { error: { status: 500, data: { message: getTrans('errors.Ошибка сервера') } } };
			const responseCheck = this.checkBuyNumberResponse(errorResponse, this.state.currentAttempt);

			if (!responseCheck.retry && responseCheck.errorMessage) {
				notification({
					type: notificationTypes.error,
					message: responseCheck.errorMessage
				});
				await this.setState({ isBuying: false });
				return;
			}

			if (responseCheck.retry) {
				const { isCancelled: cancelledBeforeRetry } = this.state;
				if (cancelledBeforeRetry) {
					await this.setState({ isBuying: false });
					this.closeBuy();
					return;
				}

				await this.setState({ isBuying: false });
				this.buyNumberTimeout = setTimeout(() => {
					this.startRepeatBuyNumber();
				}, RETRY_DELAY_MS);
			}
		}
	}
	cancelBuyNumber = async () => {
		await this.setState({ isCancelled: true });
		if (this.buyNumberTimeout) {
			clearTimeout(this.buyNumberTimeout);
			this.buyNumberTimeout = null;
		}
		const { isBuying } = this.state;
		if (!isBuying) {
			this.closeBuy();
		}
	}

	changeCountry = async (country) => {
		if (this.buyNumberTimeout) {
			clearTimeout(this.buyNumberTimeout);
			this.buyNumberTimeout = null;
		}
		await this.setState({ isBuying: true, isCancelled: true });
		const { userId, onOpenBuy } = this.props;
		const serviceSlug = country?.service_slug || this.props.config?.serviceSlug;
		const countrySlug = country?.slug || country?.country_slug;
		if (!serviceSlug || !countrySlug) {
			await this.setState({ isBuying: false, isCancelled: false });
			notification({
				type: notificationTypes.error,
				message: getTrans('errors.Ошибка сервера')
			});
			return;
		}
		try {
			const serviceInfo = await agent.get(`/api/service/get-service-data/${serviceSlug}?user=${userId}&country=${countrySlug}`)
				.then((res) => res.data)
				.catch(() => null);

			if (!serviceInfo) {
				await this.setState({ isBuying: false, isCancelled: false });
				notification({
					type: notificationTypes.error,
					message: getTrans('errors.Ошибка сервера')
				});
				return;
			}

			const countryData = serviceInfo?.country;
			const serviceData = serviceInfo?.service;
			if (!countryData || !serviceData) {
				await this.setState({ isBuying: false, isCancelled: false });
				notification({
					type: notificationTypes.error,
					message: getTrans('errors.Ошибка сервера')
				});
				return;
			}
			this.props.onClose();
			onOpenBuy({
				serviceSlug: serviceData?.slug || serviceSlug,
				serviceExtraId: serviceData?.external_id || country?.service_external_id,
				countryId: countryData?.id || country?.id,
				countrySlug: countryData?.slug || countrySlug,
				countryExtraId: countryData?.external_id || country?.external_id,
				initPrice: countryData?.price || country?.price || null
			});
		} catch (error) {
			await this.setState({ isBuying: false, isCancelled: false });
			notification({
				type: notificationTypes.error,
				message: getTrans('errors.Ошибка сервера')
			});
		}
	}
	changeCurrentPrice = async (value) => {
		if (this.buyNumberTimeout) {
			clearTimeout(this.buyNumberTimeout);
			this.buyNumberTimeout = null;
		}
		await this.setState({
			currentPrice: value,
			currentAttempt: 0,
			isBuying: false,
			isCancelled: false
		}, async () => {
			await this.startRepeatBuyNumber();
		});
	}

	closeBuy = () => {
		const { isBuying } = this.state;
		if (isBuying) {
			return;
		}
		if (this.buyNumberTimeout) {
			clearTimeout(this.buyNumberTimeout);
			this.buyNumberTimeout = null;
		}
		this.props.onClose();
	}

	render() {
		const { classes } = this.props;
		const { isLoading, isBuying, isCancelled, serviceInfo, multiPrices, currentPrice, currentAttempt } = this.state;
		const favoriteCountries = serviceInfo?.service?.favorite_countries || [];

		return (
			<Dialog
				open={true}
				onClose={this.closeBuy}
				fullScreen={true}
				className={classes.root}
				disableEscapeKeyDown={true}
			>
				<DialogContent>
					<VisibleContent visible={!isLoading}>
						<Head serviceInfo={serviceInfo} currentPrice={currentPrice} />
						<ProgressBar
							currentAttempt={currentAttempt}
							maxAttempts={MAX_ATTEMPTS}
							currentPrice={currentPrice}
							onCancel={this.cancelBuyNumber}
						/>
						<Box my={2} sx={{ opacity: "0.2" }}><Divider /></Box>
						<ContentLongWait
							prices={multiPrices}
							currentPrice={currentPrice}
							onChangePrice={this.changeCurrentPrice}
						/>
						<VisibleContent visible={Boolean(favoriteCountries.length > 0)}>
							<Box my={2} sx={{ opacity: "0.2" }}><Divider /></Box>
							<ContentCountries
								countries={favoriteCountries}
								onChange={this.changeCountry}
							/>
						</VisibleContent>
					</VisibleContent>
					<VisibleContent visible={isLoading}>
						<div></div>
					</VisibleContent>
					<LoadingOverlay isBuying={isBuying && isCancelled} />
				</DialogContent>
			</Dialog>
		);
	}
}

const Head = ({ serviceInfo }) => {
	const classes = useStyles();
	return (
		<div className={classes.head}>
			<div className={classes.headItem}>
				<div className={classes.headItemIcon}>
					<img src={getImage(serviceInfo?.service?.icon_id, { width: 56, height: 56 })} />
				</div>
				<div className={classes.headItemName}>{serviceInfo?.service?.name}</div>
			</div>
			<div className={classes.headCenter}>+</div>
			<div className={classes.headItem}>
				<div className={classes.headItemIcon}>
					<img src={getImage(serviceInfo?.country?.icon_id, { width: 56, height: 56 })} />
				</div>
				<div className={classes.headItemName}>{serviceInfo?.country?.name}</div>
			</div>
		</div>
	);
}
const ProgressBar = ({ currentAttempt, maxAttempts, currentPrice, onCancel }) => {
	const classes = useStyles();
	const progressPercent = currentAttempt > 0 ? (currentAttempt / maxAttempts) * 100 : 0;
	const formattedPrice = rateConvert(currentPrice, { useSybmol: true, decimalCount: 'auto' });

	return (
		<>
			<div className={classes.progressBar}>
				<div style={{ width: `${progressPercent}%` }} />
			</div>
			<div className={classes.progressTemp}>
				<span>{getTrans('popUpBuyNumber.Попытка')} {currentAttempt} {getTrans('popUpBuyNumber.из')} {maxAttempts} ({getTrans('popUpBuyNumber.цена')}: {formattedPrice})</span>
				<Button onClick={onCancel}>{getTrans('popUpBuyNumber.Отменить')}</Button>
			</div>
		</>
	);
}
const ContentLongWait = ({ prices, currentPrice, onChangePrice }) => {
	const classes = useStyles();
	return (
		<div className={classes.contentExtraSection}>
			<div className={classes.contentExtraSectionTitle}>{getTrans('popUpBuyNumber.Долгое ожидание?')}</div>
			<div className={classes.contentExtraSectionMessage}
				dangerouslySetInnerHTML={{
					__html: getTrans('popUpBuyNumber.Давайте попробуем найти номер дороже. Средства будут списаны окончательно только после получения СМС. Вы всегда можете отменить номер или не вводить в приложении сервиса.<br />Чем больше доплата, тем выше вероятность найти номер быстрее.')
				}}
			/>
			<div className={classes.contentExtraSectionButton}>
				{prices.map((t) => (
					<Button
						key={`button-price-${t.value}`}
						variant={Boolean(currentPrice === t.value) ? 'contained' : ''}
						onClick={onChangePrice.bind(this, t.value)}
					>{t.label}</Button>
				))}
			</div>
			{Boolean(false) && (
				<>
					<Box mt={2} />
					<div className={classes.contentExtraSectionTitle}>{getTrans('popUpBuyNumber.Или предложить свою цену')}</div>
				</>
			)}
		</div>
	);
}
const ContentCountries = ({ countries, onChange }) => {
	const classes = useStyles();

	const _renderCountryRow = (item) => {
		const countPrint = `${numberFormat(item.count)} ${getTrans('common.шт')}`;
		const pricePrint = `${rateConvert(item.price, { useSybmol: true, decimalCount: 'auto' })}`;
		return (
			<Grid item xs={12}>
				<Button
					className={classes.countryCardMain}
					onClick={onChange.bind(this, item)}
				>
					<div className={classes.countryCardMainIcon}>
						<img src={getImage(item.icon_id, { width: 32, height: 32 })} />
					</div>
					<div className={classes.countryCardMainName}>{item.name}</div>
					<div className={classes.countryCardMainCount}>{countPrint}</div>
					<div className={classes.countryCardMainBuy}>{pricePrint}</div>
				</Button>
			</Grid>
		);
	}

	return (
		<div className={classes.contentExtraSection}>
			<div className={classes.contentExtraSectionTitle}>{getTrans('popUpBuyNumber.Или смените страну:')}</div>
			<Grid container spacing={1}>
				{countries.map(_renderCountryRow)}
			</Grid>
		</div>
	);
}
const LoadingOverlay = ({ isBuying }) => {
	const classes = useStyles();
	return (
		<Backdrop
			open={isBuying}
			className={classes.loadingOverlay}
			sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
		>
			<CircularProgress sx={{ color: '#FB7800' }} />
		</Backdrop>
	);
}

const useStyles = makeStyles(theme => ({
	head: {
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "center",
	},
	headCenter: {
		width: "32px",
		height: "46px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: 24,
		color: "white",
	},
	headItem: {
		display: "flex",
		alignItems: "center",
		flexDirection: "column",
		justifyContent: "flex-start",
	},
	headItemIcon: {
		overflow: "hidden",
		borderRadius: "8px",
		position: "relative",
		width: 46,
		height: 46,
		"& img": {
			width: "100%",
			height: "100%",
			objectFit: "cover",
			objectPosition: "50% 50%",
		}
	},
	headItemName: {
		marginTop: "6px",
		fontSize: "14px",
		lineHeight: "18px",
		color: "white",
		fontWeight: "500",
		textAlign: "center",
		letterSpacing: "0.02em",
	},
	progressBar: {
		height: 32,
		marginTop: 16,
		overflow: "hidden",
		borderRadius: "8px",
		position: "relative",
		background: "#FFFFFF0D",
		border: "1px solid #FB7800",
		"& div": {
			width: "0%",
			position: "absolute",
			top: 0,
			left: 0,
			bottom: 0,
			background: "repeating-linear-gradient(-45deg, #F77600 0px, #F77600 8px, #FB9700 8px, #FB9700 16px)",
			"&.animate": {
				animation: "progressBar linear forwards",
			}
		}
	},
	progressTemp: {
		display: "flex",
		marginTop: "12px",
		alignItems: "center",
		justifyContent: "space-between",
		"& span": {
			fontSize: 14,
			lineHeight: "18px",
		},
		"& .MuiButtonBase-root": {
			fontSize: 14,
			lineHeight: "18px",
		}
	},
	contentExtraSection: {},
	contentExtraSectionTitle: {
		marginBottom: "8px",
		fontSize: 18,
		color: "white",
		fontWeight: "500",
		lineHeight: "22px",
		textAlign: "center",
	},
	contentExtraSectionMessage: {
		opacity: "0.6",
		fontSize: 14,
		color: "white",
		lineHeight: "20px",
		textAlign: "center",
	},
	contentExtraSectionButton: {
		gap: "8px",
		display: "flex",
		marginTop: "8px",
		alignItems: "center",
		justifyContent: "center",
		"& .MuiButtonBase-root": {
			border: "1px solid #FB7800",
			color: "white",
		}
	},
	countryCardMain: {
		width: "100%",
		padding: "4px",
		display: "flex",
		alignItems: "center",
		borderRadius: "12px",
		boxSizing: "border-box",
		border: "1px solid #FFFFFF1A",
		background: "#FFFFFF0D!important",
	},
	countryCardMainIcon: {
		width: 32,
		height: 32,
		display: "flex",
		overflow: "hidden",
		borderRadius: "8px",
		position: "relative",
		alignItems: "center",
		justifyContent: "center",
		"& img": {
			width: "100%",
			height: "100%",
			objectFit: "cover",
			objectPosition: "50% 50%",
		}
	},
	countryCardMainName: {
		flex: "auto",
		marginLeft: "8px",
		fontSize: 16,
		textAlign: "left",
		fontWeight: "500",
		lineHeight: "20px",
		color: theme.palette.text.primary,
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	countryCardMainCount: {
		whiteSpace: 'nowrap',
	},
	countryCardMainBuy: {
		height: "32px",
		display: "flex",
		minWidth: "64px",
		padding: "0 6px",
		marginLeft: "8px",
		borderRadius: "8px",
		alignItems: "center",
		background: "#FB7800",
		boxSizing: "border-box",
		justifyContent: "flex-start",
		color: "white",
		fontSize: "13px",
		fontWeight: "500",
		lineHeight: "16px",
	},
	loadingOverlay: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		background: "rgba(36, 50, 58, 0.8)",
	},
}))
const styles = {
	root: {
		"& .MuiPaper-root": {
			background: "#24323A",
			boxSizing: "border-box",
		},
		"& .MuiDialogContent-root": {
			boxSizing: "border-box",
		}
	}
};
const StyledPopUpBuyNumber = withStyles(styles)(PopUpBuyNumber);
const PopUpBuyNumberWrapper = (props) => {
	const { isOpen, config, handleCloseBuy, handleOpenBuy } = useBuyNumber();
	const userId = useSelector((state) => state.profile.user?.id);
	if (!isOpen) return null;
	return <StyledPopUpBuyNumber {...props} config={config} userId={userId} onClose={handleCloseBuy} onOpenBuy={handleOpenBuy} />;
};
export default PopUpBuyNumberWrapper;
